<template>
  <div style="width: 100%" :style="{height: height}">
    <b-modal v-if="showResetPasswordModal" :active.sync="showResetPasswordModal" :can-cancel="['escape', 'x']"
             :width="isMobile ? '100%' : '400px'"
             scroll="keep"
    >
      <div class="reset-password-modal">
        <div class="reset-password-header">
          <h1>Reset Password</h1>
        </div>
        <div class="reset-password-content">
          <p>Enter your email address below and if there is an account associated with it, we will send you a link to
            reset your password.</p>
          <form class="input-submit-container" @submit.prevent="sendResetPasswordEmail">
            <div class="input-submit">
              <b-field :message="emailError" :type="{ 'is-danger': isEmailInvalid }"
                       custom-class="has-text-white has-text-left"
                       expanded label="Email">
                <b-input v-model="email" expanded type="email"></b-input>
              </b-field>
              <b-button :disabled="!email" :icon-right="icon" expanded type="button"
                        variant="primary" @click="sendResetPasswordEmail">
                Send Email
              </b-button>
            </div>
          </form>
        </div>
      </div>
    </b-modal>
    <div v-if="state !== 'SIGNED_IN'" class="sign-in-container">
      <div class="sign-in-box">
        <div class="sign-in-header">
          <img class="logo" src="img/project-estimator-logo.svg">
        </div>

        <transition-group class="sign-in-content" mode="out-in" name="fade">
          <div v-show="isIe === true" key="0" class="loading-container">
            <img :src="chromeIconPath" style="margin-top: -45px; height: 100px;">
            <div class="loading-message">Please Load This Page In Chrome</div>
          </div>
          <div v-show="isIe === false && state === 'LOADING'" key="2" class="loading-container">
            <fa class="spinner" icon="spinner" size="3x" spin></fa>
            <div class="loading-message">Loading...</div>
          </div>
          <div v-show="isIe === false && state === 'SIGN_IN' && loginType === null" key="3"
               class="log-in-content">
            <div v-if="emailOnly === false" id="google-sign-in-button" class="google-sign-in">
            </div>
            <div v-if="emailOnly === false" class="or-separator">
              <div class="line"></div>
              <div class="or"> OR</div>
              <div class="line"></div>
            </div>
            <form class="input-submit-container" @submit.prevent="sendConfirmation">
              <div :class="{'email-only': emailOnly}" class="input-submit">
                <input v-model="email" placeholder="Enter your email address" type="email">
                <button :disabled="!email" class="submit-button">
                  <b-icon :icon="icon"></b-icon>
                </button>
              </div>
              <div class="sign-in-help">
                <p>Need Help Logging In?</p>
                <p>Call <a href="tel:+441359256311">01359 256311</a></p>
              </div>
            </form>
          </div>
          <div v-show="isIe === false && state === 'SIGN_IN' && loginType === 'google'" key="4"
               class="popup-prompt">
            <p>Please sign in using the pop-up window</p>
          </div>
          <div v-show="isIe === false &&  state === 'SIGN_IN_TOKEN_REQUIRED'" key="5" class="enter-token">
            <p class="token-message">Enter the code that we emailed you below</p>
            <form class="input-submit-container">
              <div class="input-submit">
                <input ref="tokenPartOne" v-model="tokenPartOne" class="token has-text-centered"
                       maxlength="3" placeholder="XXX">
                <span style="color: white; font-size: 24px">-</span>
                <input ref="tokenPartTwo" v-model="tokenPartTwo" class="token has-text-centered"
                       maxlength="3" placeholder="XXX">
              </div>
              <b-button :disabled="!tokenPartOne || !tokenPartTwo" :icon-right="icon" class="mt-2" expanded
                        type="is-secondary" @click="confirmLogin"></b-button>
              <div class="sign-in-help">
                <p>Need Help Logging In?</p>
                <p style="text-underline: #ffc520">
                  <a :href="loginGuideLink" target="_blank">Download Guide</a> or <a
                    href="tel:+441359256311">Call 01359 256311</a>
                </p>
              </div>
            </form>
          </div>
          <div v-show="isIe === false && state === 'LOGGED_OUT'" key="6" class="error-container">
            <div class="error-message" style="margin-bottom: 1em">You have signed out. Refresh to sign back in.</div>
            <button class="retry-button" style="border: none!important;" @click="reload()">
              <fa class="spinner" icon="rotate-right" size="3x"></fa>
            </button>
          </div>
          <div v-show="isIe === false && state === 'ERROR'" key="7" class="error-container">
            <!--<div key="5" class="error-container">-->
            <fa class="error-icon" icon="exclamation-triangle" size="4x"></fa>
            <p class="error-message">{{ stateReason ? stateReason : 'Error Logging In' }}</p>
            <p class="error-message more-info">For Assistance Call <a href="tel:+441359256311">01359 256311</a> or
              Email <a href="email:flightdeck@projectestimator.co.uk">flightdeck@projectestimator.co.uk</a></p>
            <button class="retry-button" @click="reload()">RETRY</button>
          </div>
        </transition-group>
      </div>
    </div>
    <slot v-else></slot>
  </div>
</template>

<script>
import Cookies from 'js-cookie';
import {STATIC_URI, STATIC_IMAGE_URI, SIGN_IN} from 'module/constants';
import PasswordHandler from "../../module/specialistStore/passwordHandler";

const LOGIN_METHOD_GOOGLE = 'google';
const LOGIN_METHOD_EMAIL_TOKEN = 'emailToken';

export default {
  name: "sign-in",
  props: {
    emailOnly: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: 'sign-in-alt'
    },
    height: {
      type: String,
      default: '100%'
    }
  },
  data() {
    return {
      email: null,
      tokenPartOne: null,
      tokenPartTwo: null,
      password: null,
      token: null,
      loginType: null,
      cookiePresent: false,
      showResetPasswordModal: false
    }
  },
  computed: {
    state() {
      return this.$store.state.user.loggedInUser.state.state ?? SIGN_IN;
    },
    isSafari() {
      return /constructor/i.test(window.HTMLElement) || (function (p) {
        return p.toString() === "[object SafariRemoteNotification]";
      })(!window['safari'] || (typeof safari !== 'undefined' && window['safari'].pushNotification));
    },
    stateReason() {
      return this.$store.state.user.loggedInUser?.state?.reason;
    },
    loginGuideLink() {
      return STATIC_URI + 'pdf/guides/FlightDeck - Login Guide.pdf'
    },
    chromeIconPath() {
      return STATIC_IMAGE_URI + 'other/chromeIcon.png';
    },
    isIe() {
      let ua = window.navigator.userAgent;
      let msie = ua.indexOf("MSIE ");
      return msie > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./);
    },
    isEmailInvalid() {
      // Simple email validation logic
      const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return this.email && !regex.test(this.email);
    },
    emailError() {
      return this.isEmailInvalid ? "This email is invalid" : "";
    },
    isMobile() {
      return this.$store.getters['device/deviceType'] === 'mobile';
    }
  },
  watch: {
    state() {
      this.stateChange();
    },
    tokenPartOne(val) {
      if (val && val.length === 3) {
        this.$refs.tokenPartTwo.focus();
      }
    },
    tokenPartTwo(val) {
      if (val && val.length === 3) {
        this.$refs.tokenPartTwo.blur();
      }
    },
  },
  created() {
    this.cookiePresent = Cookies.get(SESSION_AUTHORIZED) !== undefined;
  },
  mounted() {
    this.stateChange();
  },
  methods: {
    stateChange() {
      if (this.state === 'SIGN_IN') {
        if (this.isSafari === false && !this.emailOnly) {
          this.$store.dispatch('user/initLoginMethod', LOGIN_METHOD_GOOGLE);
        } else if (this.cookiePresent) {
          this.$store.dispatch('user/initLoginMethod', LOGIN_METHOD_EMAIL_TOKEN);
        }
      }
    },
    sendConfirmation() {
      this.$store.dispatch('user/sendConfirmation', this.email);
    },
    // confirmLogin() {
    //   if (!this.email || !this.password) {
    //     return;
    //   }
    //
    //   this.$store.dispatch('user/confirmLogin', {
    //     email: this.email,
    //     password: this.password
    //   });
    // },
    sendResetPasswordEmail() {
      if (!this.email) {
        return;
      }

      this.$store.dispatch('password-handler/resetPassword', this.email).then(() => {
        this.$buefy.snackbar.open({
          message: 'Password reset email sent',
          type: 'is-success',
          indefinite: true,
          actionText: 'OK',
          position: 'is-top',
        });
      }).catch(() => {
        this.$buefy.snackbar.open({
          message: 'Error resetting password',
          type: 'is-danger',
          indefinite: true,
          actionText: 'OK',
          position: 'is-top',
        });
      });

      this.showResetPasswordModal = false;
    },
    // openResetPasswordModal() {
    //   this.showResetPasswordModal = true;
    //
    //   this.$store.registerModule('password-handler', PasswordHandler);
    // },
    confirmLogin() {
      if (!this.tokenPartOne || !this.tokenPartTwo) {
        return;
      }

      this.$store.dispatch('user/confirmLogin', {
        email: this.email,
        token: this.tokenPartOne + '-' + this.tokenPartTwo
      });
    },
    reload() {
      window.location.reload();
    }
  }
}
</script>

<style lang="scss" scoped>
.sign-in-container {
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;

  .sign-in-box {
    display: flex;
    flex-direction: column;
    text-align: center;
    border-radius: 25px;
    background-color: white;
    width: 400px;
    -webkit-box-shadow: 4px 4px 0px 0px rgba(0, 0, 0, 0.2);
    -moz-box-shadow: 4px 4px 0px 0px rgba(0, 0, 0, 0.2);
    box-shadow: 4px 4px 0px 0px rgba(0, 0, 0, 0.2);

    @include phone {
      width: 100%;
      height: 100%;
      border-radius: 0;
    }

    .sign-in-header {
      width: 100%;
      height: 100px;
      padding: 20px 0 15px 0;
      border: 1px solid $primary;
      border-top-left-radius: 25px;
      border-top-right-radius: 25px;
      border-bottom: 0;
      background-color: $tertiary;

      @include phone {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        border: 0;
      }

      .logo {
        width: 225px;
      }
    }

    .input-submit {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0;

      &.token {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-evenly;
      }
    }

    .sign-in-help {
      color: $primary-font-color;
      font-size: 9pt;
      text-align: center;

      a {
        color: $primary-font-color;
      }
    }

    .sign-in-content {
      width: 100%;
      height: 320px;
      border-bottom-left-radius: 25px;
      border-bottom-right-radius: 25px;
      background-color: $primary;
      position: relative;

      @include phone {
        height: 100%;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }

      .log-in-content {
        padding-top: 8px;
        position: absolute;
        width: 100%;
        top: 0;
        height: 100%;
        display: flex;
        align-items: center;
        flex-direction: column;
        justify-content: center;

        .google-sign-in {
          margin: 30px auto 20px auto;
          display: flex;
          justify-content: center;
          width: 240px;
          border-radius: 161px;
          overflow: hidden;
        }

        .or-separator {
          display: flex;
          align-items: center;
          width: 220px;
          gap: 5px;

          .or {
            color: $primary-font-color;
            font-weight: 700;
            font-size: 1.1em;
          }

          .line {
            height: 2px;
            background-color: #fff;
            flex: 1;
          }
        }

        @include inputSubmit();
      }
    }

    .loading-container {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding-bottom: 12px;
      background-color: white;
      border-bottom-left-radius: 25px;
      border-bottom-right-radius: 25px;
      border: 1px solid $primary;
      border-top: 0;

      @include phone {
        display: block;
        margin-top: 50px;
      }

      .spinner {
        color: $secondary;
      }

      .loading-message {
        color: $primary-font-color;
        margin-top: 8px;
        font-weight: 500;
        font-size: 20px;
      }
    }

    .enter-token {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;

      @include phone {
        display: block;
        margin-top: 30px;
      }

      @include inputSubmit();

      .token-message {
        color: $primary-font-color;
        font-weight: 700;
      }

      input {
        text-align: center;
      }
    }

    .error-container {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      @include phone {
        display: block;
        margin-top: 30px;
        padding: 20px;
      }

      .error-icon {
        color: $primary-font-color;
        margin-bottom: 10px;
      }

      .error-message {
        color: $primary-font-color;
        font-weight: 700;
      }

      .error-message .more-info {
        font-size: .9em;
        font-weight: 500;

        a {
          color: $primary-font-color;
        }
      }

      .retry-button {
        border: 2px solid $secondary;
        border-radius: 10px;
        font-weight: 700;
        color: $primary-font-color;
        background-color: $primary;
        padding: 5px 10px;
        transition: all .3s ease;
        cursor: pointer;

        @include phone() {
          width: 100%;
          margin-top: 50px;
        }

        &:hover {
          background-color: $secondary;
          color: $primary;
        }
      }
    }

    .popup-prompt {
      display: flex;
      justify-content: center;
      margin-top: 50px;
      height: 100%;
      color: $primary-font-color;
      font-weight: 700;
      font-size: 1.1em;
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0
}

.log-in-content.fade-enter-active,
.popup-prompt.fade-enter-active,
.error-container.fade-enter-active,
.enter-token.fade-enter-active,
.loading-container.fade-enter-active {
  transition-delay: .6s;
}

.reset-password-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: $primary;
  border-radius: 25px;

  .reset-password-header {
    width: 100%;
    text-align: center;
    margin-bottom: 20px;

    h1 {
      color: $primary-font-color;
      font-weight: 700;
      font-size: 1.5em;
    }
  }

  .reset-password-content {
    width: 100%;
    text-align: left;

    p {
      color: $primary-font-color;
      font-weight: 500;
      font-size: 1.1em;
      margin-bottom: 20px;
    }

    @include inputSubmit();

    .input-submit-container {
      width: 100% !important;
    }
  }
}
</style>